import { LitElement, html, css, ifDefined, classMap } from '@hh/global.js'

class HHCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    position: {
      type: String,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
    _focusedIndex: {
      state: true,
    },
  }

  constructor() {
    super()
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.uuid = Math.random() * (1000000 - 1) + 1
    this.position = 'bottom'
    this._focusedIndex = 0
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this.handleBodyClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.removeEventListener('click', this.handleBodyClick)
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (path.some((node) => node === this)) return
    if (this._active) this.toggle()
  }

  toggle(e = false) {
    if (e && e.type === 'keyup' && !['ESCAPE'].includes(e.code.toUpperCase())) {
      return
    }
    this._active = !this._active
    if (this._active) {
      this.requestUpdate()
    } else {
      // When closing, return focus to the trigger button
      this.updateComplete.then(() => {
        const triggerButton = this.renderRoot.querySelector('.button')
        triggerButton && triggerButton.focus()
      })
    }
  }

  selectValue(e, value) {
    e.preventDefault()
    this._selected = value
    const hiddenInput = this.querySelector('[slot="input"]')
    if (hiddenInput) hiddenInput.value = value

    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: { value },
      })
    )

    if (this._active) this.toggle() // close after selection
  }

  handleKeydown(e) {
    const optionsCount = this.options.length

    if (!this._active) {
      // When closed, only handle Enter/Space to open the dropdown
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        // Enter/Space when closed opens the listbox
        this.toggle()
      }
      return
    }

    if (e.key === 'Tab') {
      e.preventDefault()
      return
    }

    const listItems = this.renderRoot.querySelectorAll('.option')

    // If open:
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this._focusedIndex = (this._focusedIndex + 1) % optionsCount
      listItems[this._focusedIndex]?.focus()
      this.requestUpdate()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this._focusedIndex =
        (this._focusedIndex - 1 + optionsCount) % optionsCount
      listItems[this._focusedIndex]?.focus()
      this.requestUpdate()
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      // Pressing enter/space selects the currently focused option
      this.selectValue(e, this.options[this._focusedIndex].value)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      this.toggle()
    }
  }

  get selectedLabel() {
    const option = this.options.find((opt) => opt.value === this._selected)
    return option ? option.label : 'Invalid selected option'
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  render() {
    const titleClasses = {
      selected: this._selected !== undefined,
    }
    return html`
      <div @keydown="${this.handleKeydown}">
        <div class="container">
          <div id="${this.uuid}-label" class=${classMap(titleClasses)}>
            <slot name="title"></slot>
          </div>

          <button
            @click="${this.toggle}"
            type="button"
            class="button"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded="${this._active || false}"
            aria-controls="${this.uuid}-list"
            aria-labelledby="${this.uuid}-label"
            aria-activedescendant="${this._active
              ? `${this.uuid}-option-${this._focusedIndex}`
              : ''}"
            part="button"
          >
            <span part="button-label" class="button__label">
              ${this.selectedLabel}
            </span>
            <slot name="icon"></slot>
          </button>
          <slot name="input"></slot>
          <hh-focus-trap active="${this._active}">
            <ul
              part="list"
              id="${this.uuid}-list"
              class="${classMap({
                list: true,
                'is-active': this._active,
                'position-top': this.position === 'top',
              })}"
              aria-hidden="${!this._active}"
              role="listbox"
              aria-labelledby="${this.uuid}-label"
            >
              ${this.options.map((option, index) => {
                const isSelected = option.value === this._selected
                return html`
                  <li
                    id="${this.uuid}-option-${index}"
                    tabindex="-1"
                    class="${classMap({
                      option: true,
                      'is-focused': index === this._focusedIndex,
                      'is-active': isSelected,
                    })}"
                    role="option"
                    aria-selected="${isSelected ? 'true' : 'false'}"
                    @click="${(e) => this.selectValue(e, option.value)}"
                  >
                    ${this.sanitize(option.label)}
                  </li>
                `
              })}
            </ul>
          </hh-focus-trap>
        </div>
      </div>
    `
  }

  static styles = css`
    :host {
      width: 100%;
    }

    .container {
      position: relative;
    }

    .button {
      align-items: center;
      background: transparent;
      color: inherit;
      cursor: pointer;
      display: flex;
      font: var(--hh-body-font);
      height: var(--hh-height, 35px);
      justify-content: space-between;
      padding: 0;
      width: 100%;
      border: none;
      border-bottom: var(--hh-border-width, 1px) solid currentColor;
    }

    .button__label {
      position: relative;
      top: 8px;
      font: var(--hh-body-2-font);
    }

    ::slotted([slot='button']),
    ::slotted([slot='icon']) {
      align-items: center;
      display: flex;
      height: 100%;
    }

    ::slotted([slot='icon']) {
      margin-left: 30px;
      width: 10px;
    }

    ::slotted([slot='title']) {
      color: var(--hh-color-subdued);
      font: var(--hh-utility-3);
      left: 0;
      margin: 0;
      position: absolute;
      top: 5px;
    }

    .selected ::slotted([slot='title']) {
      font-size: 12px !important;
    }

    .list {
      background-color: var(--hh-background, white);
      border: var(--hh-border-width, 1px) solid currentColor;
      display: block;
      margin: 0;
      max-height: var(--hh-options-max-height, 19rem);
      max-width: var(--hh-options-max-width, 22rem);
      min-height: var(--hh-options-min-height, 8.2rem);
      min-width: var(--hh-options-min-width, 12rem);
      opacity: 0;
      overflow-y: auto;
      padding-left: 0;
      position: absolute;
      transform: translateY(0);
      transition: visibility var(--hh-duration-default, 300ms)
          var(--hh-duration-default, 300ms),
        opacity var(--hh-duration-default, 300ms),
        transform var(--hh-duration-default, 300ms);
      visibility: hidden;
      width: 100%;
      z-index: 3;
    }

    .list.position-top {
      bottom: 100%;
    }

    .list.is-active {
      opacity: 1;
      transform: translateY(5px);
      transition: transform var(--hh-duration-default, 300ms) 1ms,
        opacity var(--hh-duration-default, 300ms) 1ms, visibility 1ms;
      visibility: visible;
    }

    .option {
      list-style: none;
      padding: 1em;
      cursor: pointer;
    }
    .option.is-active {
      opacity: 0.5;
    }

    @media screen and (min-width: 750px) {
      .button__label {
        font: var(--hh-body-font);
      }
    }
  `
}

customElements.define('hh-custom-select', HHCustomSelect)
